import firebase from "firebase/compat/app"
import "firebase/compat/auth"
import "firebase/compat/firestore"
import "firebase/compat/storage"
import "firebase/compat/database"
import "firebase/compat/analytics"
import { FirestoreProxy } from "firestore-interceptor"
import { getEnv } from "../../utils/env"

// Create a singleton to ensure Firebase is only initialized once
class FirebaseInitializer {
  private static instance: FirebaseInitializer
  private initialized = false

  public readyApp: firebase.app.App
  public db: ReturnType<typeof FirestoreProxy.getInstance>
  public auth: firebase.auth.Auth
  public firestore: typeof firebase.firestore
  public analytics: firebase.analytics.Analytics
  public ref: firebase.database.Reference
  public storage: typeof firebase.storage
  public sto: firebase.storage.Storage
  public database: firebase.database.Database
  public FirestoreTimestamp: typeof firebase.firestore.Timestamp

  private constructor() {
    try {
      console.log("Starting Firebase initialization...")

      // Initialize Firebase only once
      const firebaseConfig = {
        apiKey: getEnv("API_KEY"),
        authDomain: getEnv("AUTH_DOMAIN"),
        databaseURL: getEnv("DB_URL"),
        projectId: getEnv("PROJECT_ID"),
        storageBucket: getEnv("BUCKET_URL"),
        messagingSenderId: getEnv("MESSAGING_SENDER_ID"),
        appId: getEnv("APP_ID"),
        measurementId: getEnv("MEASUREMENTID")
      }

      // Check if Firebase is already initialized
      try {
        const existingApp = firebase.app()
        console.log(
          "Firebase already initialized, using existing app:",
          existingApp.name
        )
        this.readyApp = existingApp
      } catch (e) {
        // Initialize app if not already initialized
        console.log("Initializing new Firebase app...")
        this.readyApp = firebase.initializeApp(firebaseConfig)
      }

      // Initialize services
      console.log("Initializing Firebase services...")
      this.auth = firebase.auth()
      this.firestore = firebase.firestore

      try {
        this.analytics = firebase.analytics()
      } catch (e) {
        console.warn("Analytics initialization failed:", e)
        this.analytics = null as any
      }

      // Initialize FirestoreProxy with all required parameters
      console.log("Initializing FirestoreProxy...")
      const cloudFunctionRoot = getEnv("CLOUD_FUNCTION_ROOT")
      if (!cloudFunctionRoot) {
        console.warn(
          "CLOUD_FUNCTION_ROOT env variable not set! Using empty string as fallback."
        )
      }

      const ourFirestore = firebase.firestore()

      // Super hacky workaround for changing firestore database
      ;(
        ourFirestore as unknown as { _databaseId: { database: string } }
      )._databaseId.database = getEnv("FIRESTORE_DB", "(default)")

      this.db = FirestoreProxy.getInstance(
        ourFirestore,
        this.auth,
        cloudFunctionRoot || "",
        firebase.firestore.Timestamp
      )
      console.log("FirestoreProxy initialized successfully")

      // Initialize other Firebase services
      console.log("Initializing additional Firebase services...")
      this.ref = firebase.database().ref()
      this.storage = firebase.storage
      this.sto = firebase.storage()
      this.database = firebase.database().goOffline()
      this.FirestoreTimestamp = firebase.firestore.Timestamp

      this.initialized = true
      console.log("Firebase initialization complete!")
    } catch (error) {
      console.error("Error during Firebase initialization:", error)
      throw error // Re-throw to make initialization errors visible
    }
  }

  // Patch the typesaurus browser adapter to use our FirestoreProxy instance
  // public patchTypesaurusBrowserAdapter() {
  //   try {
  //     // Attempt to patch the typesaurus adapter at runtime
  //     // This is a hack to work around the issue in the typesaurus browser adapter
  //     const typesaurusStore = require("typesaurus/adaptor/browser").default;
  //     const originalStore = typesaurusStore;

  //     // Override the store function to return our initialized FirestoreProxy instance
  //     require("typesaurus/adaptor/browser").default = function() {
  //       console.log("Using patched Typesaurus browser adapter");
  //       return FirebaseInitializer.getInstance().db;
  //     };

  //     console.log("Successfully patched Typesaurus browser adapter");
  //     return true;
  //   } catch (error) {
  //     console.error("Failed to patch Typesaurus browser adapter:", error);
  //     return false;
  //   }
  // }

  public static getInstance(): FirebaseInitializer {
    if (!FirebaseInitializer.instance) {
      try {
        console.log("Creating Firebase initializer singleton instance")
        FirebaseInitializer.instance = new FirebaseInitializer()
        console.log("Firebase singleton instance created successfully")
      } catch (error) {
        console.error("Failed to initialize Firebase:", error)
        throw new Error(`Firebase initialization failed: ${error.message}`)
      }
    } else {
      console.log("Reusing existing Firebase initializer instance")
    }
    return FirebaseInitializer.instance
  }
}

// Export a singleton instance
export const firebaseInit = FirebaseInitializer.getInstance()

// Export individual services for convenience
export const {
  readyApp,
  db,
  auth,
  firestore,
  analytics,
  ref,
  storage,
  sto,
  database,
  FirestoreTimestamp
} = firebaseInit

// Default export for firebase itself
export default firebase
